import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import { PermitsTabTemplate as template } from './permits-government-approvals.template';
import { PermitsTabStyles as styles } from './permits-government-approvals.styles';
import { Select } from '@genesislcap/foundation-zero';
import { CASCADE_ENUM, EnumTranslate, IEnumService } from '../../../../../services/enum.service';
import { Documents } from '../../../../../types';
import { IProjectModalService } from '../../../../../services';
import { clearErrorUtil, displayErrorUtil, PROJECT_OPERATIONAL_STATUS } from '../../../../../utils';

const name = 'permits-tab';

const documentStatusDefault = CASCADE_ENUM.DOCUMENT_STATUS_DEFAULT;

@customElement({
  name,
  template,
  styles,
})
export class PermitsTab extends FASTElement {
  @observable landControlAgreements: Select;
  @observable landControlAgreementsValue: String = '';
  @observable titleInsurance: Select;
  @observable titleInsuranceValue: String = '';
  @observable litigationDisclosure: Select;
  @observable litigationDisclosureValue: String = '';
  @observable landTitleAssociationSurvey: Select;
  @observable landTitleAssociationSurveyValue: String = '';
  @observable emergencyManagement: Select;
  @observable emergencyManagementValue: String = '';
  @observable phaseIEnvironmentalReview: Select;
  @observable phaseIEnvironmentalReviewValue: String = '';
  @observable wetlandsDelineationReview: Select;
  @observable speciesReview: Select;
  @observable speciesReviewValue: String = '';
  @observable wetlandsDelineationReviewValue: String = '';
  @observable culturalResourcesStudyReview: Select;
  @observable culturalResourcesStudyReviewValue: String = '';
  @observable primaryStateLevelPermit: Select;
  @observable primaryStateLevelPermitValue: String = '';
  @observable primaryCountyLevelPermit: Select;
  @observable primaryCountyLevelPermitValue: String = '';
  @observable primaryTownshipLevelPermit: Select;
  @observable primaryTownshipLevelPermitValue: String = '';
  @observable taxAbatementApplication: Select;
  @observable taxAbatementApplicationValue: String = '';
  @observable DnhFromFaa: Select;
  @observable DnhFromFaaValue: String = '';

  // ENUMs
  @observable statusEnum: Array<EnumTranslate>;
  @observable submissionStatusEnum: Array<EnumTranslate>;

  // Services
  @IEnumService enumService: IEnumService;

  @IProjectModalService projectModalService: IProjectModalService;

  public _requiredFormFields = [];

  connectedCallback() {
    super.connectedCallback();

    this._requiredFormFields = [
      this.landControlAgreements,
      this.titleInsurance,
      this.litigationDisclosure,
      this.landTitleAssociationSurvey,
      this.emergencyManagement,
      this.phaseIEnvironmentalReview,
      this.wetlandsDelineationReview,
      this.speciesReview,
      this.culturalResourcesStudyReview,
      this.primaryStateLevelPermit,
      this.primaryCountyLevelPermit,
      this.primaryTownshipLevelPermit,
      this.taxAbatementApplication,
      this.DnhFromFaa,
    ];

    this.loadEnums();
    this.attachedBlurEvent();
  }

  private attachedBlurEvent() {
    this._requiredFormFields.forEach((field) => {
      field.addEventListener('blur', () => this.checkFieldValidity(field));
      field.addEventListener('change', () => this.checkFieldValidity(field));
    });
  }

  private checkTabCompletionStatus() {
    const tabCompleted = this._requiredFormFields.every((elm) =>
      this.projectModalService.totalCompletedFields.includes(elm)
    );
    this.$emit('permitsTabCompleted', tabCompleted);
  }

  private checkFieldValidity(elm: Select) {
    if (!this.enumService.isDocumentEnumSelected(elm.value)) {
      displayErrorUtil(elm);
      this.amendValidList(elm, false);
    } else {
      clearErrorUtil(elm);
      this.amendValidList(elm, true);
    }
  }

  private amendValidList(field: Select, isValid: boolean) {
    if (isValid) {
      this.projectModalService.addToTotalCompletedFields(field);
    } else {
      this.projectModalService.removeFromTotalCompletedFields(field);
    }
    this.checkTabCompletionStatus();
  }

  private async loadEnums() {
    this.enumService.getEnumValues(CASCADE_ENUM.PROJECT_DRAFT_STATUS).then((response) => {
      if (response.REPLY) {
        this.statusEnum = response.REPLY;
      }
    });

    this.enumService.getEnumValues(CASCADE_ENUM.PROJECT_SUBMISSION_STATUS).then((response) => {
      if (response.REPLY) {
        this.submissionStatusEnum = response.REPLY;
      }
    });
  }

  public populateForm(document: Documents) {
    this.addRequiredFieldsToService();

    this.landControlAgreementsValue = document.LAND_CONTROL_AGREEMENTS;
    this.amendValidList(this.landControlAgreements, this.enumService.isDocumentEnumSelected(this.landControlAgreementsValue));

    this.titleInsuranceValue = document.TITLE_INSURANCE;
    this.amendValidList(this.titleInsurance, this.enumService.isDocumentEnumSelected(this.titleInsuranceValue));

    this.litigationDisclosureValue = document.LITIGATION_DISCLOSURE;
    this.amendValidList(this.litigationDisclosure, this.enumService.isDocumentEnumSelected(this.litigationDisclosureValue));

    this.landTitleAssociationSurveyValue = document.LAND_TITLE_SURVEY;
    this.amendValidList(this.landTitleAssociationSurvey, this.enumService.isDocumentEnumSelected(this.landTitleAssociationSurveyValue));

    this.emergencyManagementValue = document.FLOOD_HAZARD_DETERMINATION;
    this.amendValidList(this.emergencyManagement, this.enumService.isDocumentEnumSelected(this.emergencyManagementValue));

    this.phaseIEnvironmentalReviewValue = document.ESA_REVIEW;
    this.amendValidList(this.phaseIEnvironmentalReview, this.enumService.isDocumentEnumSelected(this.phaseIEnvironmentalReviewValue));

    this.speciesReviewValue = document.THREATENED_ENDANGERED_SPECIES_REVIEW;
    this.amendValidList(this.speciesReview, this.enumService.isDocumentEnumSelected(this.speciesReviewValue));

    this.wetlandsDelineationReviewValue = document.WETLANDS_DELINEATION_REVIEW;
    this.amendValidList(this.wetlandsDelineationReview, this.enumService.isDocumentEnumSelected(this.wetlandsDelineationReviewValue));

    this.culturalResourcesStudyReviewValue = document.CULTURAL_RESOURCES_STUDY_REVIEW;
    this.amendValidList(this.culturalResourcesStudyReview, this.enumService.isDocumentEnumSelected(this.culturalResourcesStudyReviewValue));

    this.primaryStateLevelPermitValue = document.PRIMARY_STATE_LEVEL_PERMIT;
    this.amendValidList(this.primaryStateLevelPermit, this.enumService.isDocumentEnumSelected(this.primaryStateLevelPermitValue));

    this.primaryCountyLevelPermitValue = document.PRIMARY_COUNTY_LEVEL_PERMIT;
    this.amendValidList(this.primaryCountyLevelPermit, this.enumService.isDocumentEnumSelected(this.primaryCountyLevelPermitValue));

    this.primaryTownshipLevelPermitValue = document.PRIMARY_TOWNSHIP_LEVEL_PERMIT;
    this.amendValidList(this.primaryTownshipLevelPermit, this.enumService.isDocumentEnumSelected(this.primaryTownshipLevelPermitValue));

    this.taxAbatementApplicationValue = document.TAX_ABATEMENT_APPLICATION;
    this.amendValidList(this.taxAbatementApplication, this.enumService.isDocumentEnumSelected(this.taxAbatementApplicationValue));

    this.DnhFromFaaValue = document.DNH_FROM_FAA;
    this.amendValidList(this.DnhFromFaa, this.enumService.isDocumentEnumSelected(this.DnhFromFaaValue));
  }

  public clearForm() {
    const allFields = [
      this.landControlAgreements,
      this.titleInsurance,
      this.litigationDisclosure,
      this.landTitleAssociationSurvey,
      this.emergencyManagement,
      this.phaseIEnvironmentalReview,
      this.wetlandsDelineationReview,
      this.speciesReview,
      this.culturalResourcesStudyReview,
      this.primaryStateLevelPermit,
      this.primaryCountyLevelPermit,
      this.primaryTownshipLevelPermit,
      this.taxAbatementApplication,
      this.DnhFromFaa,
    ];

    allFields.forEach((field) => {
      if (field) {
        clearErrorUtil(field);
      }
    });

    this.landControlAgreementsValue = documentStatusDefault;
    this.landControlAgreementsValue = documentStatusDefault;
    this.titleInsuranceValue = documentStatusDefault;
    this.litigationDisclosureValue = documentStatusDefault;
    this.landTitleAssociationSurveyValue = documentStatusDefault;
    this.emergencyManagementValue = documentStatusDefault;
    this.phaseIEnvironmentalReviewValue = documentStatusDefault;
    this.wetlandsDelineationReviewValue = documentStatusDefault;
    this.speciesReviewValue = documentStatusDefault;
    this.culturalResourcesStudyReviewValue = documentStatusDefault;
    this.primaryStateLevelPermitValue = documentStatusDefault;
    this.primaryCountyLevelPermitValue = documentStatusDefault;
    this.primaryTownshipLevelPermitValue = documentStatusDefault;
    this.taxAbatementApplicationValue = documentStatusDefault;
    this.DnhFromFaaValue = documentStatusDefault;

    this.$emit('permitsTabCompleted', false);
  }

  public resetValues() {
    this.landControlAgreementsValue = documentStatusDefault;
    this.titleInsuranceValue = documentStatusDefault;
    this.litigationDisclosureValue = documentStatusDefault;
    this.landTitleAssociationSurveyValue = documentStatusDefault;
    this.emergencyManagementValue = documentStatusDefault;
    this.phaseIEnvironmentalReviewValue = documentStatusDefault;
    this.speciesReviewValue = documentStatusDefault;
    this.wetlandsDelineationReviewValue = documentStatusDefault;
    this.culturalResourcesStudyReviewValue = documentStatusDefault;
    this.primaryStateLevelPermitValue = documentStatusDefault;
    this.primaryCountyLevelPermitValue = documentStatusDefault;
    this.primaryTownshipLevelPermitValue = documentStatusDefault;
    this.taxAbatementApplicationValue = documentStatusDefault;
    this.DnhFromFaaValue = documentStatusDefault;
  }

  public addRequiredFieldsToService() {
    // update Service with latest required fields
    this.projectModalService.addToRequiredFormField(this._requiredFormFields);
  }

  public removeRequiredFieldsToService() {
    // update Service with latest required fields
    this.projectModalService.removeFromRequiredFormField(this._requiredFormFields);
  }
}
